<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ElDatePicker } from 'element-plus'
import {
  adminDateTimeRangeShortcuts,
  type AdminDateTimeRangeShortcutItem
} from '../adminDateTimeRangeShortcuts'

defineOptions({ name: 'AdminDateTimeRange' })

const emit = defineEmits<{
  calendarChange: [val: [Date, Date | null]]
  visibleChange: [visible: boolean]
}>()

const range = defineModel<string[]>({ default: () => [] })

const props = withDefaults(
  defineProps<{
    startPlaceholder?: string
    endPlaceholder?: string
    valueFormat?: string
    disabled?: boolean
    clearable?: boolean
    /** 在横向工具栏里占满剩余空间（flex），默认开启 */
    fillRow?: boolean
    /**
     * 与 Element Plus `show-confirm` 一致（2.11+ 原生默认多为 `true`）。
     * - `false`（本组件默认）：**不用点「确定」**，起止都选好即自动写回输入框并更新 v-model。
     * - `true`：面板显示「确定」，需点击后才写回。
     */
    showConfirm?: boolean
    /** 透传 `ElDatePicker`：`true` 表示该日历格不可选 */
    disabledDate?: (date: Date) => boolean
    /**
     * 面板左侧快捷时间；`false` 关闭；未传时用内置（今天/昨天/最近7天/最近30天/本月）。
     */
    shortcuts?: false | AdminDateTimeRangeShortcutItem[]
    /** `true` 时仅选日期（`daterange`），不展示时分秒 */
    dateOnly?: boolean
  }>(),
  {
    startPlaceholder: '开始时间',
    endPlaceholder: '结束时间',
    valueFormat: 'YYYY-MM-DD HH:mm:ss',
    clearable: true,
    fillRow: true,
    showConfirm: false,
    disabledDate: undefined,
    shortcuts: undefined,
    dateOnly: false
  }
)

const pickerType = computed(() => (props.dateOnly ? 'daterange' : 'datetimerange'))

const resolvedValueFormat = computed(() => (props.dateOnly ? 'YYYY-MM-DD' : props.valueFormat))

const resolvedShortcuts = computed(() => {
  if (props.shortcuts === false) return undefined
  if (props.shortcuts != null && props.shortcuts.length) return props.shortcuts
  return adminDateTimeRangeShortcuts
})

/** 选中新日期时：开始默认 00:00:00，结束默认 23:59:59 */
const dayBoundaryDefaultTime: [Date, Date] = [
  new Date(2000, 0, 1, 0, 0, 0),
  new Date(2000, 0, 1, 23, 59, 59)
]

/**
 * 与 ElDatePicker 直接 v-model，避免受控模式下选第一项时被 `length !== 2` 误清空，
 * 从而在起止都选好后能立刻写回输入框。
 */
const inner = ref<[string, string] | undefined>()

watch(
  () => range.value,
  (r) => {
    const next = r?.length === 2 && r[0] && r[1] ? ([r[0], r[1]] as [string, string]) : undefined
    const cur = inner.value
    if (!next) {
      if (cur !== undefined) inner.value = undefined
      return
    }
    if (!cur || cur[0] !== next[0] || cur[1] !== next[1]) {
      inner.value = next
    }
  },
  { deep: true, immediate: true }
)

watch(inner, (v) => {
  if (v && Array.isArray(v) && v.length === 2 && v[0] && v[1]) {
    const r = range.value
    if (r.length !== 2 || r[0] !== v[0] || r[1] !== v[1]) {
      range.value = [v[0], v[1]]
    }
  } else if (v == null || (Array.isArray(v) && !v.length)) {
    if (range.value.length) range.value = []
  }
})

const onCalendarChange = (val: [Date, Date | null]) => {
  emit('calendarChange', val)
}

const onVisibleChange = (visible: boolean) => {
  emit('visibleChange', visible)
}
</script>

<template>
  <div
    class="admin-date-time-range"
    :class="{
      'admin-date-time-range--fill-row': fillRow,
      'admin-date-time-range--date-only': dateOnly
    }"
  >
    <ElDatePicker
      v-model="inner"
      class="admin-date-time-range__picker"
      :type="pickerType"
      :value-format="resolvedValueFormat"
      :default-time="dateOnly ? undefined : dayBoundaryDefaultTime"
      :start-placeholder="startPlaceholder"
      :end-placeholder="endPlaceholder"
      :shortcuts="resolvedShortcuts"
      :disabled="disabled"
      :disabled-date="disabledDate"
      :clearable="clearable"
      :show-confirm="showConfirm"
      @calendar-change="onCalendarChange"
      @visible-change="onVisibleChange"
    />
  </div>
</template>

<style scoped>
.admin-date-time-range {
  min-width: 400px;
  max-width: 640px;
  width: 100%;
  box-sizing: border-box;
}

/* daterange 只显示日期，两个 `YYYY-MM-DD` 框 + 分隔符 ~240px 就够，min-width 400 会强撑过宽，
   破坏行内搜索表单 label 对齐 → daterange 用 240。 */
.admin-date-time-range--date-only {
  min-width: 240px;
}

.admin-date-time-range--fill-row {
  flex: 1 1 400px;
}

.admin-date-time-range--date-only.admin-date-time-range--fill-row {
  flex: 1 1 240px;
}

.admin-date-time-range__picker {
  width: 100%;
}

.admin-date-time-range__picker :deep(.el-date-editor) {
  width: 100%;
  box-sizing: border-box;
}
</style>
